import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert, Avatar } from 'flowbite-react';
import { useAsync } from 'react-async-hook';
import { FiEdit2, FiSave, FiX } from 'react-icons/fi';
import * as sched from 'sched';
import { useAuth } from '../../hooks/useAuth';

interface UserProfileFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UserProfileForm: React.FC<UserProfileFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user, token, client } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUserEditedAvatar, setHasUserEditedAvatar] = useState(false);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string>('');
  
  // Refs for editable elements
  const displayNameRef = useRef<HTMLDivElement>(null);
  const avatarUrlRef = useRef<HTMLDivElement>(null);

  // Fetch current user profile
  const { result: currentProfile, loading, error: fetchError } = useAsync(async () => {
    if (!user || !token || !client || !isOpen) return null;

    try {
      const profile = await (sched.WrappedApiClient as any).get_user_profile(
        client,
        token,
        user.userId
      );
      return profile;
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      // Return null instead of throwing - this indicates no profile exists yet
      return null;
    }
  }, [user, token, client, isOpen]);

  // Reset editing state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setSubmitError(null);
      setSubmitSuccess(null);
      setHasUserEditedAvatar(false);
      setCurrentAvatarUrl(currentProfile?.avatar_url || '');
    }
  }, [isOpen, currentProfile]);

  // Update editable content when profile loads or when creating new profile
  useEffect(() => {
    if (displayNameRef.current && avatarUrlRef.current) {
      if (currentProfile) {
        displayNameRef.current.textContent = currentProfile.display_name || '';
        avatarUrlRef.current.textContent = currentProfile.avatar_url || '';
        setCurrentAvatarUrl(currentProfile.avatar_url || '');
      } else if (isEditing) {
        // For new profiles, start with empty content
        displayNameRef.current.textContent = '';
        avatarUrlRef.current.textContent = '';
        setCurrentAvatarUrl('');
      }
    }
  }, [currentProfile, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHasUserEditedAvatar(false);
    // Reset content to original values
    if (displayNameRef.current && avatarUrlRef.current) {
      if (currentProfile) {
        displayNameRef.current.textContent = currentProfile.display_name || '';
        avatarUrlRef.current.textContent = currentProfile.avatar_url || '';
        setCurrentAvatarUrl(currentProfile.avatar_url || '');
      } else {
        // For new profiles, clear the content
        displayNameRef.current.textContent = '';
        avatarUrlRef.current.textContent = '';
        setCurrentAvatarUrl('');
      }
    }
  };

  const handleSave = async () => {
    if (!user || !token || !client || !displayNameRef.current || !avatarUrlRef.current) {
      setSubmitError('Authentication required');
      return;
    }

    const displayName = displayNameRef.current.textContent?.trim() || '';
    const avatarUrl = avatarUrlRef.current.textContent?.trim() || '';

    if (!displayName) {
      setSubmitError('Display name is required');
      return;
    }

    // Validate avatar URL if provided
    if (avatarUrl && !isValidUrl(avatarUrl)) {
      setSubmitError('Please enter a valid avatar URL');
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      // Prepare the update request
      const updateRequest = {
        display_name: displayName,
        email: null, // Don't update email
        avatar_url: avatarUrl || null,
      };

      // Call the update API
      await sched.WrappedApiClient.update_user_profile(
        client,
        token,
        user.userId,
        updateRequest
      );

      setSubmitSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Call success callback and close modal after a brief delay
      setTimeout(() => {
        onSuccess?.();
        onClose();
        setSubmitSuccess(null);
      }, 1500);

    } catch (err) {
      console.error('Failed to update profile:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isEditing) {
      handleCancel();
    }
    setSubmitError(null);
    setSubmitSuccess(null);
    onClose();
  };

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleDisplayNameBlur = () => {
    if (!displayNameRef.current || !avatarUrlRef.current) return;
    
    // Only apply auto-generation for new profiles (when currentProfile is null)
    if (currentProfile) return;
    
    const displayName = displayNameRef.current.textContent?.trim() || '';
    const currentAvatarText = avatarUrlRef.current.textContent?.trim() || '';
    
    if (!displayName) return;
    
    // Generate the default URL for this display name
    const defaultUrl = getDefaultUserProfileAvatarUrl(displayName);
    
    // Only auto-set if:
    // 1. User hasn't manually edited the avatar field
    // 2. Current avatar is empty OR matches a previously generated default
    const shouldAutoSet = !hasUserEditedAvatar && 
      (currentAvatarText === '' || currentAvatarText === currentAvatarUrl || 
       currentAvatarText.startsWith('https://api.dicebear.com/9.x/fun-emoji/svg?seed='));
    
    if (shouldAutoSet) {
      avatarUrlRef.current.textContent = defaultUrl;
      setCurrentAvatarUrl(defaultUrl);
    }
  };

  const handleAvatarUrlBlur = () => {
    if (!avatarUrlRef.current) return;
    
    const avatarUrl = avatarUrlRef.current.textContent?.trim() || '';
    setCurrentAvatarUrl(avatarUrl);
  };

  const handleAvatarUrlInput = () => {
    // Mark that user has manually edited the avatar field
    setHasUserEditedAvatar(true);
  };

  if (!isOpen) return null;

  return (
    <Modal show={isOpen} onClose={handleClose} size="md">
      <ModalHeader>User Profile</ModalHeader>
      <ModalBody>
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <span className="ml-2 text-cm-text-secondary">Loading profile...</span>
          </div>
        )}

        {fetchError && (
          <Alert color="failure" className="mb-4">
            Failed to load profile: {fetchError.message}
          </Alert>
        )}

        {submitError && (
          <Alert color="failure" className="mb-4">
            {submitError}
          </Alert>
        )}

        {submitSuccess && (
          <Alert color="success" className="mb-4">
            {submitSuccess}
          </Alert>
        )}

{!loading && !fetchError && (
          currentProfile ? (
            // Existing profile view
            <div className="space-y-6">
              {/* Profile Header with Avatar and Display Name */}
              <div className="flex items-center space-x-4">
                <Avatar
                  img={currentAvatarUrl || currentProfile.avatar_url || undefined}
                  size="lg"
                  className="flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <div
                      ref={displayNameRef}
                      contentEditable={isEditing}
                      suppressContentEditableWarning={true}
                      onBlur={isEditing ? handleDisplayNameBlur : undefined}
                      className={`text-xl font-semibold text-cm-text-primary ${
                        isEditing 
                          ? 'bg-cm-bg-elevated border border-cm-border-subtle rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500' 
                          : 'cursor-default'
                      }`}
                      style={{ minWidth: '200px', minHeight: '32px' }}
                    >
                      {currentProfile.display_name}
                    </div>
                    {!isEditing && (
                      <Button
                        size="xs"
                        color="gray"
                        onClick={handleEdit}
                        className="p-1"
                      >
                        <FiEdit2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-cm-text-primary mb-1">
                  Email Address
                </label>
                <div className="text-cm-text-primary bg-cm-bg-elevated px-3 py-2 rounded border border-cm-border-subtle">
                  {currentProfile.email}
                </div>
                <p className="text-xs text-cm-text-muted mt-1">
                  Email cannot be changed from this interface
                </p>
              </div>

              {/* Avatar URL */}
              <div>
                <label className="block text-sm font-medium text-cm-text-primary mb-1">
                  Avatar URL
                </label>
                <div
                  ref={avatarUrlRef}
                  contentEditable={isEditing}
                  suppressContentEditableWarning={true}
                  onInput={isEditing ? handleAvatarUrlInput : undefined}
                  onBlur={isEditing ? handleAvatarUrlBlur : undefined}
                  className={`text-cm-text-primary min-h-[40px] px-3 py-2 rounded border overflow-hidden whitespace-nowrap ${
                    isEditing 
                      ? 'bg-cm-bg-surface border-cm-border-subtle focus:outline-none focus:ring-2 focus:ring-blue-500' 
                      : 'bg-cm-bg-elevated border-cm-border-subtle cursor-default'
                  }`}
                  data-placeholder={isEditing ? 'https://example.com/avatar.jpg' : ''}
                >
                  {currentProfile.avatar_url}
                </div>
                {isEditing && !currentProfile.avatar_url && (
                  <p className="text-xs text-cm-text-muted mt-1">
                    Enter a URL for your avatar image (optional)
                  </p>
                )}
              </div>

              {/* Created Date */}
              <div>
                <label className="block text-sm font-medium text-cm-text-primary mb-1">
                  Member Since
                </label>
                <div className="text-cm-text-primary bg-cm-bg-elevated px-3 py-2 rounded border border-cm-border-subtle">
                  {new Date(currentProfile.created_date).toLocaleDateString()}
                </div>
              </div>
            </div>
          ) : (
            // New profile creation view
            <div className="space-y-6">
              <div className="text-center py-4">
                <h3 className="text-lg font-medium text-cm-text-primary mb-2">
                  Create Your Profile
                </h3>
                <p className="text-cm-text-secondary mb-4">
                  You don't have a profile yet. Let's create one to get started.
                </p>
                {!isEditing && (
                  <Button onClick={handleEdit} className="mb-4">
                    Create Profile
                  </Button>
                )}
              </div>

              {isEditing && (
                <>
                  {/* Avatar Preview */}
                  <div className="flex justify-center mb-6">
                    <Avatar
                      img={currentAvatarUrl || undefined}
                      size="lg"
                      className="flex-shrink-0"
                    />
                  </div>

                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-cm-text-primary mb-1">
                      Display Name *
                    </label>
                    <div
                      ref={displayNameRef}
                      contentEditable={true}
                      suppressContentEditableWarning={true}
                      onBlur={handleDisplayNameBlur}
                      className="text-cm-text-primary min-h-[40px] px-3 py-2 rounded border bg-cm-bg-surface border-cm-border-subtle focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ minWidth: '100%' }}
                    />
                    <p className="text-xs text-cm-text-muted mt-1">
                      Enter your display name (required)
                    </p>
                  </div>

                  {/* Avatar URL */}
                  <div>
                    <label className="block text-sm font-medium text-cm-text-primary mb-1">
                      Avatar URL (optional)
                    </label>
                    <div
                      ref={avatarUrlRef}
                      contentEditable={true}
                      suppressContentEditableWarning={true}
                      onInput={handleAvatarUrlInput}
                      onBlur={handleAvatarUrlBlur}
                      className="text-cm-text-primary min-h-[40px] px-3 py-2 rounded border bg-cm-bg-surface border-cm-border-subtle focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-hidden whitespace-nowrap"
                    />
                    <p className="text-xs text-cm-text-muted mt-1">
                      Enter a URL for your avatar image (optional)
                    </p>
                  </div>
                </>
              )}
            </div>
          )
        )}
      </ModalBody>
      
      <ModalFooter>
        {isEditing ? (
          <div className="flex space-x-2">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-1"
            >
              <FiSave className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </Button>
            <Button
              color="gray"
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center space-x-1"
            >
              <FiX className="w-4 h-4" />
              <span>Cancel</span>
            </Button>
          </div>
        ) : (
          <Button color="gray" onClick={handleClose}>
            Close
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UserProfileForm;

const getDefaultUserProfileAvatarUrl = (name: string) => {
  return "https://api.dicebear.com/9.x/fun-emoji/svg?seed=" + encodeURIComponent(name);
}
  